import { z } from "zod";

// Phase 11 typed configSchema for the `shopping-list-aggregator` template.
// This REPLACES the sole untyped (unknown-shaped) placeholder that remained in
// TEMPLATE_CATALOG (Plan 02 swaps this schema in). validateRecipe Pass 2A runs
// configSchema.safeParse(widget.config); with this retype, no catalog entry leaves
// its config root untyped any longer.
//
// What the template does (described generically — the C1 grep gate bans the
// domain name): it aggregates ingredient lines across the options a recipe selects
// plus its sizing passes into a single consolidated list, optionally grouped and
// annotated with footnotes/totals.
//
// Load-bearing contract: the existing canonical fixture's config for this template
// supplies a `grouping` value (the first member of the grouping enum below) that
// MUST parse verbatim so recipes authored before Plan 02's catalog retype keep
// validating. The grouping enum therefore includes that existing-fixture value.
//
// Generality (the C1 grep gate): every field name and every comment here is generic —
// aggregateFrom / grouping / footnotes / totals. The values in aggregateFrom are
// widget instance-id refs (the sources to aggregate over); the recipe declares them.
//
// DoS bounds (T-11-01-01): aggregateFrom.min(1).max(20), footnotes.max(20),
// totals.max(20); every string length-bounded. Bounds an unbounded extracted config
// before it reaches storage.
//
// Cross-widget existence checks (do the aggregateFrom ids resolve to real widget
// instances?) are recipe-root-walk territory (future Pass 2B), not enforced here.
//
// isolatedDeclarations + verbatimModuleSyntax + exactOptionalPropertyTypes (D-15):
// every exported type is an explicit interface and the exported const carries a
// `z.ZodType<ShoppingListAggregatorConfig>` annotation so tsc emits declarations
// without reading across module boundaries (no TS9013). `grouping` is required on
// the output type; `.default("none")` makes it optional on input while keeping the
// output non-optional (mirrors summary-checklist.viewMode).

export interface ShoppingListAggregatorFootnote {
  id: string;
  label: string;
}

export interface ShoppingListAggregatorConfig {
  aggregateFrom: ReadonlyArray<string>;
  grouping: "aisle" | "category" | "none"; // grep-allow: aisle
  footnotes?: ReadonlyArray<ShoppingListAggregatorFootnote> | undefined;
  totals?: ReadonlyArray<string> | undefined;
}

// Optional footnote schema — kebab-case id + label. Bounded list on the root.
const _footnoteSchema = z.strictObject({
  id: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
  label: z.string().min(1).max(200),
});

// Top-level schema: aggregateFrom.min(1): aggregating over zero sources is
// meaningless; .max(20): DoS bound. grouping is a closed enum whose FIRST member
// is the existing-fixture value; .default("none") keeps a config that omits
// grouping valid without breaking the fixture (which supplies that value
// explicitly).
// Optional footnotes/totals are bounded; the footnotes superRefine emits a
// per-index custom issue for duplicate footnote ids.
export const ShoppingListAggregatorConfigSchema: z.ZodType<ShoppingListAggregatorConfig> = z
  .strictObject({
    aggregateFrom: z.array(z.string().min(1).max(100)).min(1).max(20),
    grouping: z.enum(["aisle", "category", "none"]).default("none"), // grep-allow: aisle
    footnotes: z.array(_footnoteSchema).max(20).optional(),
    totals: z.array(z.string().min(1).max(100)).max(20).optional(),
  })
  .superRefine((cfg, ctx) => {
    if (cfg.footnotes === undefined) {
      return;
    }
    const seenIds = new Set<string>();
    for (const [i, footnote] of cfg.footnotes.entries()) {
      if (seenIds.has(footnote.id)) {
        ctx.addIssue({
          code: "custom",
          path: ["footnotes", i, "id"],
          message: `duplicate footnote id "${footnote.id}"`,
        });
      }
      seenIds.add(footnote.id);
    }
  });
